import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useData } from '../DataProviderContext';
import { ROUTES } from '../routes';
import { requiresEmailVerification } from '../authVerification';
import { copyPublishedTemplate, loadPublishedTemplate } from './templateRepository';

const pageSx = {
  bgcolor: 'grey.50',
  color: 'text.primary',
  minHeight: 'calc(100vh - 64px)',
  textAlign: 'left',
};

const containerSx = {
  width: '100%',
  maxWidth: 1120,
  px: { xs: 2, sm: 3, md: 4 },
  py: { xs: 3, sm: 4, md: 6 },
};

export default function TemplatePreviewPage() {
  const { templateId } = useParams();
  const { authState } = useAuth();
  const { dispatch } = useData();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, template: null, error: '', copying: false });
  const resumed = useRef(false);
  useEffect(() => {
    let active = true;
    loadPublishedTemplate(templateId)
      .then((template) => active && setState((current) => ({ ...current, loading: false, template, error: template ? '' : 'This template is not available.' })))
      .catch(() => active && setState((current) => ({ ...current, loading: false, error: 'This template could not be loaded.' })));
    return () => { active = false; };
  }, [templateId]);

  const handleUseTemplate = useCallback(async () => {
    if (!authState.user) {
      navigate(ROUTES.login, { state: { from: location, useTemplate: templateId } });
      return;
    }
    if (requiresEmailVerification(authState.user)) {
      navigate(ROUTES.verifyEmail, { state: { from: location } });
      return;
    }
    if (state.copying) return;
    setState((current) => ({ ...current, copying: true, error: '' }));
    try {
      const copied = await copyPublishedTemplate(authState.user.uid, templateId);
      dispatch({
        type: 'LOAD_GOAL_WORKING_COPY',
        payload: {
          data: copied.data,
          journeyMeta: copied.journeyMeta,
          activeGoal: { goal: copied.title, journeyId: copied.journeyId, source: 'firestore-imported' },
          storageStatus: 'Saved to cloud',
        },
      });
      navigate(ROUTES.mainView, { replace: true });
    } catch {
      setState((current) => ({ ...current, copying: false, error: 'A private journey could not be created from this template. Please try again.' }));
    }
  }, [authState.user, dispatch, location, navigate, state.copying, templateId]);

  useEffect(() => {
    if (!state.loading && state.template && authState.user && location.state?.useTemplate === templateId && !resumed.current) {
      resumed.current = true;
      void handleUseTemplate();
    }
  }, [authState.user, handleUseTemplate, location.state, state.loading, state.template, templateId]);

  if (state.loading) {
    return (
      <Box sx={pageSx}>
        <Container maxWidth={false} sx={containerSx}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress aria-label="Loading template" />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!state.template) {
    return (
      <Box sx={pageSx}>
        <Container maxWidth={false} sx={containerSx}>
          <Alert severity="info">{state.error}</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={pageSx} data-testid="template-preview-page">
      <Container maxWidth={false} sx={containerSx}>
        {state.error && <Alert severity="error" sx={{ mb: 3 }}>{state.error}</Alert>}

        <Paper
          component="section"
          aria-labelledby="template-title"
          variant="outlined"
          data-testid="template-hero"
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 2,
            borderTop: 6,
            borderTopColor: 'primary.main',
            boxShadow: 1,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 3, md: 5 }}
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
            justifyContent="space-between"
          >
            <Stack spacing={2.25} sx={{ flex: 1, minWidth: 0 }}>
              <Box>
                <Chip
                  label={state.template.category}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 1.5, fontWeight: 700 }}
                />
                <Typography
                  id="template-title"
                  variant="h3"
                  component="h1"
                  sx={{
                    maxWidth: '20ch',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    fontWeight: 850,
                    lineHeight: 1.08,
                    letterSpacing: '-0.025em',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {state.template.title}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ mt: 2, maxWidth: '70ch', fontSize: { xs: '1rem', sm: '1.075rem' }, lineHeight: 1.7 }}
                >
                  {state.template.description}
                </Typography>
              </Box>

              <Box
                data-testid="template-goal"
                sx={{
                  maxWidth: 620,
                  px: { xs: 2, sm: 2.5 },
                  py: 1.75,
                  bgcolor: 'action.hover',
                  borderLeft: 4,
                  borderLeftColor: 'primary.main',
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: 'block', fontWeight: 800, lineHeight: 1.4 }}
                >
                  Goal
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{ mt: 0.5, fontWeight: 750, lineHeight: 1.35, overflowWrap: 'anywhere' }}
                >
                  {state.template.goal}
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="contained"
              size="large"
              disabled={state.copying}
              onClick={handleUseTemplate}
              data-testid="use-template"
              sx={{
                minHeight: 48,
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: 200 },
                flexShrink: 0,
                fontWeight: 800,
                whiteSpace: 'nowrap',
                '&.Mui-focusVisible': {
                  outline: '3px solid',
                  outlineColor: 'primary.light',
                  outlineOffset: 3,
                },
              }}
            >
              {state.copying ? 'Creating…' : 'Use this template'}
            </Button>
          </Stack>
        </Paper>

        <Box
          component="section"
          aria-labelledby="template-steps-heading"
          sx={{ mt: { xs: 5, md: 7 }, width: '100%', maxWidth: 920, mx: 'auto' }}
        >
          <Typography
            id="template-steps-heading"
            variant="h4"
            component="h2"
            sx={{ fontWeight: 850, letterSpacing: '-0.015em' }}
          >
            What you&apos;ll work through
          </Typography>

          <Stack spacing={3} sx={{ mt: 3, width: '100%' }}>
            {state.template.steps.map((step, index) => (
              <Card
                component="article"
                key={step.id}
                variant="outlined"
                data-testid="template-step"
                sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1 }}
              >
                <CardContent
                  sx={{
                    p: { xs: 2.5, sm: 3.5, md: 4 },
                    '&:last-child': { pb: { xs: 2.5, sm: 3.5, md: 4 } },
                  }}
                >
                  <Chip label={`Step ${index + 1}`} size="small" color="primary" sx={{ mb: 1.5, fontWeight: 800 }} />
                  <Typography
                    variant="h5"
                    component="h3"
                    sx={{ maxWidth: '28ch', fontWeight: 800, lineHeight: 1.25, overflowWrap: 'anywhere' }}
                  >
                    {step.title}
                  </Typography>
                  {step.description && (
                    <Typography color="text.secondary" sx={{ mt: 1.25, maxWidth: '72ch', lineHeight: 1.7 }}>
                      {step.description}
                    </Typography>
                  )}

                  {step.actionItems.length > 0 && (
                    <Stack
                      component="ol"
                      divider={<Divider flexItem />}
                      sx={{ p: 0, m: 0, mt: 2.5, listStyle: 'none', borderTop: 1, borderTopColor: 'divider' }}
                    >
                      {step.actionItems.map((item, actionIndex) => (
                        <Box component="li" key={item.id} data-testid="template-action" sx={{ py: 2.25 }}>
                          <Stack direction="row" spacing={1.75} alignItems="flex-start">
                            <Box
                              aria-hidden="true"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 28,
                                height: 28,
                                flex: '0 0 28px',
                                mt: 0.125,
                                borderRadius: '50%',
                                bgcolor: 'action.selected',
                                color: 'text.secondary',
                                fontSize: '0.8rem',
                                fontWeight: 800,
                              }}
                            >
                              {actionIndex + 1}
                            </Box>
                            <Box sx={{ minWidth: 0, maxWidth: '72ch' }}>
                              <Typography sx={{ fontWeight: 650, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
                                {item.title}
                              </Typography>
                              {item.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 0.5, lineHeight: 1.65, overflowWrap: 'anywhere' }}
                                >
                                  {item.description}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
